// Unified login — one form for both Admin and Espace Membre.
// Backend `/api/auth/connect` returns the correct redirect (`/apleo` or
// `/espace`) based on the credentials matched.
import { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import PasswordInput from "@/components/forms/PasswordInput";

const COPY = {
  fr: {
    kicker: "Espace réservé",
    title: "Connexion",
    sub: "Connectez-vous à votre espace : membres, bénévoles, organisateurs, artistes ou administrateurs — un seul formulaire pour tous les profils.",
    email: "Adresse e-mail",
    password: "Mot de passe",
    submit: "Se connecter",
    forgot: "Mot de passe oublié ?",
    join: "Pas encore inscrit·e ?",
    register: "Créer un compte membre",
    error: "Identifiants invalides.",
    inactive: "Compte désactivé. Contactez un organisateur.",
  },
  en: {
    kicker: "Reserved area",
    title: "Sign in",
    sub: "Sign in to your space — members, volunteers, organisers, artists or administrators. One form for all profiles.",
    email: "Email address",
    password: "Password",
    submit: "Sign in",
    forgot: "Forgot password?",
    join: "Not registered yet?",
    register: "Create a member account",
    error: "Invalid credentials.",
    inactive: "Account disabled. Please contact an organiser.",
  },
};

export default function Connexion() {
  const { locale } = useI18n();
  const t = COPY[locale] || COPY.fr;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/connect", { email, password });
      const target = data.redirect_to || (data.kind === "admin" ? "/apleo" : "/espace");
      // Hard navigate so AuthContext / MemberAuthContext re-hydrate with the
      // newly-set cookie. SPA navigation would keep stale `admin=false` state.
      window.location.assign(target);
    } catch (ex) {
      const msg = ex?.response?.data?.detail;
      setError(ex?.response?.status === 403 ? t.inactive : (msg || t.error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="connexion-page" className="py-16 px-4"
         style={{ background: "var(--parchment-2)", minHeight: "calc(100vh - 12rem)" }}>
      <div className="container-prieure max-w-md">
        <form onSubmit={onSubmit} className="illuminated-border bg-white p-8">
          <div className="text-center mb-6">
            <div className="font-caps text-[0.65rem]" style={{ color: "var(--gold)", letterSpacing: "0.28em" }}>
              {t.kicker}
            </div>
            <h1 className="font-display text-3xl mt-2">{t.title}</h1>
            <p className="font-body text-sm mt-3" style={{ color: "var(--muted)" }}>
              {t.sub}
            </p>
          </div>

          <label className="label-caps text-[0.6rem]">{t.email}</label>
          <input
            data-testid="connexion-email"
            required type="email" value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="input-classic mt-1 mb-4 w-full"
            autoComplete="email"
          />

          <label className="label-caps text-[0.6rem]">{t.password}</label>
          <div className="mt-1 mb-2">
            <PasswordInput
              testId="connexion-password" required value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
          </div>

          <div className="text-right mb-4">
            <Link to="/espace/mot-de-passe-oublie"
                  data-testid="connexion-forgot"
                  className="font-caps text-[0.6rem]"
                  style={{ color: "var(--lapis)", letterSpacing: "0.18em" }}>
              {t.forgot}
            </Link>
          </div>

          {error && (
            <p data-testid="connexion-error" className="font-body text-sm mb-3"
               style={{ color: "var(--cinnabar)" }}>
              {error}
            </p>
          )}

          <button
            data-testid="connexion-submit"
            type="submit" disabled={busy}
            className="btn btn-primary w-full">
            {busy ? "…" : t.submit}
          </button>

          <div className="mt-6 pt-6 border-t text-center" style={{ borderColor: "var(--border)" }}>
            <p className="font-body text-sm" style={{ color: "var(--muted)" }}>
              {t.join}{" "}
              <Link to="/espace/inscription" data-testid="connexion-register"
                    className="link-underline" style={{ color: "var(--cinnabar)" }}>
                {t.register}
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
